import React, { useState, useEffect } from 'react';
import { Button } from '@uhg-abyss/web/ui/Button';
import { Drawer } from '@uhg-abyss/web/ui/Drawer';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { usePropsContext } from '@uhg-abyss/web/hooks/usePropsContext';
import { styled } from '@uhg-abyss/web/tools/styled';
import { PayerGroup } from './PayerGroup';
import { usePayers } from '../../../../redux/resources/payers';
import { useUser } from '../../../../redux/resources/user';
import { usercache } from '../../../../redux/tools/usercache';
import { toastHandler } from '../../../../common/constants/toasts/toastHandler';
import { usePPDLagoon } from '@sde/widgets-core/src/tools/lagoon';
import { useToast } from '@uhg-abyss/web/hooks/useToast';

const StyledPayerDrawer = styled(Drawer, {
  '&.abyss-modal-overlay': {
    zIndex: 999999,
  },
  '&.abyss-modal-content-container': {
    backgroundColor: '$gray1',
  },
  '&.abyss-modal-footer': {
    borderTop: '1px solid $gray4',
    backgroundColor: '$white',
  },
});

export const PayerSelectDrawer = () => {
  const { getMessage } = usePPDLagoon();
  const [isOpen, setIsOpen] = useState(false);
  const { data, events } = usePropsContext();
  const { payers } = usePayers.state();
  const { getPayers, setPayer } = usePayers.actions();
  const user = useUser.state();
  const { isPayerOpen } = data;
  const [defaultPayer, setDefaultPayer] = useState();
  const [accordionState, setAccordionState] = useState('payer-group');
  const { toast } = useToast();
  const payerAndProviderSelectionSuccessToastMessage = getMessage(
    'payer-and-provider-selection-success-toast-message'
  );

  const [selectedPayerRow, setSelectedPayerRow] = useState({
    payerId: '',
    payerName: '',
    payerLabel: '',
  });
  const [payerNameSearchValue, setPayerNamerSearchValue] = useState('');
  const [payerIdSearchValue, setPayerIdSearchValue] = useState('');
  const setSelectedPayer = (payerId, list) => {
    let selectedPayer;
    if (payerId) {
      selectedPayer = list.find((payer) => payer.id === payerId);
    }

    if (!selectedPayer) {
      const [firstPayer] = list;
      selectedPayer = firstPayer;
    }

    const payer = selectedPayer
      ? {
          payerId: selectedPayer.id,
          payerName: selectedPayer.name,
          payerLabel: selectedPayer.label,
        }
      : null;

    setDefaultPayer(payer);
    usercache.set('payer', payer);

    return payer;
  };

  const handlePayerRowClick = (payer) => {
    if (payer.label === selectedPayerRow.payerLabel) {
      return;
    }

    const newPayer = {
      payerId: payer.id,
      payerName: payer.name,
      payerLabel: payer.label,
    };

    setSelectedPayerRow(newPayer);
    events('changePayerStartEvent');
    setAccordionState('');
  };

  function isPayerInSearch(payerObj) {
    const [payerId, payerName] = payerObj.label.split(' - ');
    if (!payerName) {
      return payerId.toLowerCase().includes(payerNameSearchValue.toLowerCase());
    }
    return (
      payerName.toLowerCase().includes(payerNameSearchValue.toLowerCase()) &&
      (payerIdSearchValue === '' ||
        payerId.toLowerCase().includes(payerIdSearchValue.toLowerCase()))
    );
  }

  const filteredPayerData = payers.filter(isPayerInSearch);

  const handleAccordionClicked = (e) => {
    setAccordionState(e);
  };

  const handleReset = () => {
    setSelectedPayerRow(defaultPayer);
    setPayerNamerSearchValue('');
    setPayerIdSearchValue('');
    setAccordionState('payer-group');
  };

  const handleClose = () => {
    handleReset();
    setIsOpen(false);
    events('onDrawerClose');
  };

  const handleCancel = () => {
    handleReset();
    setIsOpen(false);
    events('onDrawerClose');
  };

  const handleSave = () => {
    const payer = {
      payerId: selectedPayerRow.payerId,
      payerName: selectedPayerRow.payerName,
      payerLabel: selectedPayerRow.payerLabel,
    };

    setPayer(payer);
    setDefaultPayer(payer);
    usercache.set('payer', payer);
    events('onChange', payer);
    events('onSave', payer);
    toast.show(
      toastHandler(
        'Payer selection updated',
        payerAndProviderSelectionSuccessToastMessage,
        'success'
      )
    );
    setIsOpen(false);
    setPayerNamerSearchValue('');
    setPayerIdSearchValue('');
  };

  useEffect(() => {
    if (isPayerOpen) {
      setAccordionState('payer-group');
    }
    setIsOpen(isPayerOpen);
  }, [isPayerOpen]);

  useEffect(() => {
    const cachedPayer = usercache.get('payer');
    if (cachedPayer) {
      events('onChange', cachedPayer);
    }

    getPayers(
      { application: data.application, isIntakeUser: user.isIntakeUser },
      (payersData) => {
        events('onSuccess', payersData);
      },
      (err) => {
        events('onError', err);
      }
    );
  }, [data.application]);

  useEffect(() => {
    if (payers.length > 0) {
      const cachedPayer = usercache.get('payer');
      const cachedPayerId = cachedPayer && cachedPayer.payerId;
      const selectedPayerId = data.payerId || cachedPayerId;
      const payer = setSelectedPayer(selectedPayerId, payers);

      setPayer(payer);
      setSelectedPayerRow(payer);
      events('onChange', payer);
    }
  }, [data.payerId, payers]);

  return (
    <React.Fragment>
      <StyledPayerDrawer
        title='Select a payer'
        isOpen={isOpen}
        size='650px'
        onClose={handleClose}
        footer={
          <Drawer.Section>
            <Layout.Group alignLayout='right'>
              <Button
                data-testid='payer-drawer-cancel-button'
                variant='outline'
                onClick={handleCancel}
              >
                Cancel
              </Button>
              <Button
                data-testid='payer-drawer-save-button'
                onClick={handleSave}
              >
                Save
              </Button>
            </Layout.Group>
          </Drawer.Section>
        }
      >
        <Drawer.Section>
          <PayerGroup
            payerNameSearchValue={payerNameSearchValue}
            setPayerNameSearchValue={setPayerNamerSearchValue}
            payerIdSearchValue={payerIdSearchValue}
            setPayerIdSearchValue={setPayerIdSearchValue}
            filteredPayerData={filteredPayerData}
            selectedPayerRow={selectedPayerRow}
            handlePayerRowClick={handlePayerRowClick}
            accordionState={accordionState}
            handleAccordionClicked={handleAccordionClicked}
          />
        </Drawer.Section>
      </StyledPayerDrawer>
    </React.Fragment>
  );
};
